// import { hostUrl } from '../../../';

import {read_cookie} from "sfcookies";
import {HttpError} from "react-admin";
import {hostUrl, UseUrlManagerCleanUrl} from "../../components/utils/url-manager";

/**
 * What a refused request should say, when the body does not say it better.
 */
const REFUSAL_MESSAGES: Record<number, string> = {
    401: 'Your session has expired. Please sign in again.',
    403: 'You do not have permission to do that.',
    404: 'That record could not be found.',
    422: 'The server would not accept those values.',
};

/**
 * The reason a response was refused, preferring whatever the API said.
 *
 * API Platform answers a hydra error with `hydra:description`; the customer-facing routes
 * answer `{status, message}`. Both are checked before falling back to a status-shaped
 * sentence, because the API's own wording is nearly always the more useful one.
 */
function refusalReason(response: Response, body: any): string {
    const said: unknown = body?.['hydra:description'] ?? body?.detail ?? body?.message;
    if (typeof said === 'string' && said.trim() !== '') {
        return said;
    }

    return REFUSAL_MESSAGES[response.status] ?? `The request was refused (${response.status}).`;
}

/**
 * The parsed body of a successful response, or a rejection carrying the status.
 *
 * HQ-06. Every method of every data provider used to run
 * `if ((status < 200 || status >= 300) && status < 500) { }` - a status test with an empty
 * body, the real one commented out - and then parse the response regardless. A 401 payload
 * has no `hydra:member`, so the provider handed react-admin `{data: [], total: 0}` and the
 * screen rendered a successful empty list. Nothing ever reached `authProvider.checkError`,
 * which is why a dead admin token looked like a working panel full of nothing rather than a
 * signed-out session. 381 occurrences across 55 files, all the same shape.
 *
 * `HttpError` is what react-admin expects: `message` is what the notification shows and
 * `status` is what `checkError` reads to decide on a logout.
 */
export async function readJsonOrRefuse(response: Response): Promise<any> {
    // Read the body once. A second .json() on a consumed body throws, and that error would
    // then be the one the screen reported instead of the real refusal.
    const body: any = await response.json().catch((): any => ({}));
    if (!response.ok) {
        throw new HttpError(refusalReason(response, body), response.status, body);
    }

    return body;
}

/**
 * The same check for a method that ignores the response body - `delete`, which answers
 * `{data: params}` whatever comes back.
 */
export async function refuseOnHttpError(response: Response): Promise<Response> {
    if (!response.ok) {
        const body: any = await response.json().catch((): any => ({}));
        throw new HttpError(refusalReason(response, body), response.status, body);
    }

    return response;
}

export const processGetManyFilter: (url: string, params: any) => string = (url: string, params: any): string => {
    let string: string = '';
    for (let i: number = 0; i < params['ids'].length; i++) {

        let param: any = params['ids'][i];
        if (typeof parseInt(param) === "number") {
            if (string === '') {
                string += 'id[]=' + param;
            } else {
                string += '&id[]=' + param;
            }
        } else {
            let temp: string = param.replace('/noknok_api/', '');
            let values: string[] = temp.split('/');
            if (values.length === 2) {
                if (string === '') {
                    string += values[0] + '[]=' + values[1];
                } else {
                    string += '&' + values[0] + '[]=' + values[1];
                }
            }
        }
    }
    if (string.length > 0) {
        if (url.indexOf('?') > -1) {
            url += '&' + string;
        } else {
            url += '?' + string;
        }
    }
    return url;
}

export const processGetManyByReferenceChildren: (data: any, type: string) => string[]|null = (data: any, type: string) : string[]|null => {
    if (Array.isArray(data)) {
        let newArray: string[] = [];
        data.forEach( (datum: string): void => {
            let tmp: string = datum.replace('/hq_api/' + type + '/', '');
            newArray.push(tmp);
        });
        if (newArray.length) {
            return newArray;
        }
    }
    return null;
}

export const processGetManyByReferenceFilter: (url: string, params: any) => string = (url: string, params: any): string => {
    // debugger;
    let string: string = '';
    if (Array.isArray(params['id'])) {
        if (params['id'].length === 0) {
            params['id'] = [0];
            // Sometimes get reference many has an empty array.
            // Give it a zero ID to filter out all values
        }

        for (let i: number = 0; i < params['id'].length; i++) {

            let param: any = params['id'][i];
            if (typeof parseInt(param) === "number") {
                if (string === '') {
                    string += 'id[]=' + param;
                } else {
                    string += '&id[]=' + param;
                }
            }
        }
    }

    if (params.target !== undefined && string === "" && !Array.isArray(params['id'])) {
        if (url.includes('?')) {
            url += '&' + params.target + '[]=' + params.id;
        } else {
            url += '?' + params.target + '[]=' + params.id;
        }

    }
    if (string.length === 0 && params.target === 'id' && typeof params.id === "string") {
        url += '&id[]=' + params.id
    }

    if (typeof params.filter !== "undefined") {
        for (const key in params.filter) {
            if (params.filter.hasOwnProperty(key)) {

                if (string === '') {
                    string += key + '=' + params.filter[key];
                } else {
                    string += '&' + key + '=' + params.filter[key];
                }
            }
        }
    }

    if (string.length > 0) {
        if (url.indexOf('?') > -1) {
            url += '&' + string;
        } else {
            url += '?' + string;
        }
    }
    return url;
}

/**
 * Append a react-admin list's filters to a collection URL.
 *
 * HQ-58. Values are encoded, so a `+`, `&`, `#` or `%` typed into a filter reaches HQ-API as typed
 * instead of ending or corrupting the query. An array is sent as `key[]=a&key[]=b`, which is what
 * API Platform's SearchFilter reads as "any of"; it used to arrive with a stray empty `key=` in
 * front, or joined with commas when it was the first filter. The separator used to be chosen with
 * `url.indexOf('?')` as a boolean, which is true at -1. Keys are left as they are, because some
 * carry API Platform's own brackets (`order[name]`). A nested object is sent under dotted keys. An
 * unset value is left out rather than sent as the text "undefined".
 */
/**
 * The bounds of API Platform's `DateFilter`, which it reads only in bracket form
 * (`createdAt[after]`). An input with that source reaches `processFilters` nested, like a dotted
 * one, and sent dotted it was ignored: every row came back (HQ-59).
 */
const DATE_BOUNDS: ReadonlySet<string> = new Set(['after', 'before', 'strictly_after', 'strictly_before']);

export const processFilters: (url: string, params: any) => string = (url: string, params: any): string => {
    const pairs: string[] = [];
    const add: (key: string, value: any) => void = (key: string, value: any): void => {
        if (value === undefined || value === null) {
            return;
        }
        if (Array.isArray(value)) {
            for (const item of value) {
                pairs.push(key + '[]=' + encodeURIComponent(String(item)));
            }
        } else if (typeof value === 'object') {
            // A filter input with a dotted source (`accountHolder.name`) reaches here nested, and
            // API Platform wants the dotted key back.
            for (const child of Object.keys(value)) {
                add(DATE_BOUNDS.has(child) ? key + '[' + child + ']' : key + '.' + child, value[child]);
            }
        } else {
            pairs.push(key + '=' + encodeURIComponent(String(value)));
        }
    };
    const filter: any = params?.filter ?? {};
    for (const key of Object.keys(filter)) {
        add(key, filter[key]);
    }
    if (pairs.length === 0) {
        return url;
    }
    return url + (url.includes('?') ? '&' : '?') + pairs.join('&');
};

export const processAccountFilter: (url: string, params: any) => string = (url: string, params: any): string => {
    let string: string = '';
    if (typeof params.filter !== "undefined") {
        for (const key in params.filter) {
            if (params.filter.hasOwnProperty(key)) {
                if (string === '') {
                    string += key.toLowerCase() + '=' + params.filter[key];
                } else {
                    string += '&' + key.toLowerCase() + '=' + params.filter[key];
                }
            }
        }
        if (string.length > 0) {
            if (url.indexOf('?')) {
                url += '&' + string;
            } else {
                url += '?' + string;
            }
        }
    }
    return url;
};

export function addUrlToUri(values: string[], url: string): string[] {
    // debugger;
    if (values === undefined) return;
    let returnValue: string[] = values;
    let tmp: string[] = [];
    returnValue.forEach( (itemValue: string): void => {
        tmp.push((url + itemValue));
    });
    if (tmp.length > 0) {
        returnValue = tmp;
    }
    return returnValue
}

export function removeUrlFromUri(values: string[], url: string): string[] {
    // debugger;
    if (values === undefined) return;
    let returnValue: string[] = values;
    let tmp: string[] = [];
    returnValue.forEach( (itemValue: string): void => {
        tmp.push((itemValue.replace(url, '')));
    });
    if (tmp.length > 0) {
        returnValue = tmp;
    }
    return returnValue
}

export function cleanEntityString(string: string, url: string): string {
    if (url === undefined || string === undefined) {
        return undefined;
    }
    return string.replace(url, '');
}

export function restoreEntityString(string: string, url: string): string {
    if (url === undefined || string === undefined) {
        return undefined;
    }
    return url + string;
}

export const convertFileToBase64: (file: any) => Promise<any> = (file: any): Promise<any> => {
    if (typeof file.src === "undefined" || file === "" || file.src === "") {
        return new Promise( (resolve: (value: any) => void, reject: (reason?: any) => void): void => {
            resolve('');
        });
    } else if (file.src.startsWith('images/')) {
        return new Promise( (resolve: (value: any) => void, reject: (reason?: any) => void): void => {
            resolve(file.src);
        });
    } else {
        return new Promise( (resolve: (value: any) => void, reject: (reason?: any) => void): void => {
            const reader: FileReader = new FileReader();
            reader.onload = (): void => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file.rawFile);
        });
    }
}

export function addSortToUrl(url: string, params: any): string {
    if (params.sort !== undefined && params.sort.field !== undefined) {
        if (url.indexOf('?')) {
            url += '&sortOrder[' + params.sort.field + ']=' + params.sort.order;
        } else {
            url += '?sortOrder[' + params.sort.field + ']=' + params.sort.order;
        }
    }
    return url;
}

/**
 * HQAPI-83. This used to take the destination directory from each caller and post it as
 * `image_path` / `video_path`, and HQ-API used that unchecked: a path with `..` in it escaped the
 * web root, and nothing looked at the file type. The caller names the upload target now and
 * HQ-API's own `UploadTargets` decides both the directory and which ACL verb to require, so a
 * directory is no longer something this side can choose or get wrong.
 */
export async function createUploadFormData(
    params: any,
    keys: { image: string, video: string, captions?: string } = { image: 'image', video: 'video' },
    uploadTarget: string
): Promise<any> {
    const formData: FormData = new FormData();
    let hasUpload: boolean = false;
    if (params.data[keys.image] !== undefined && params.data[keys.image]?.rawFile) {
        formData.append('image', params.data[keys.image].rawFile, params.data[keys.image].rawFile.name);
        hasUpload = true;
    }
    if (params.data[keys.video] !== undefined && params.data[keys.video]?.rawFile) {
        formData.append('video', params.data[keys.video]?.rawFile);
        hasUpload = true;
    }
    // Only targets HQ-API's UploadTargets gives a captions directory accept this; others refuse it.
    if (keys.captions !== undefined && params.data[keys.captions]?.rawFile) {
        formData.append('captions', params.data[keys.captions].rawFile, params.data[keys.captions].rawFile.name);
        hasUpload = true;
    }
    if (hasUpload) {
        formData.append('resource', uploadTarget);
    }
    if (hasUpload) {
        let token: string = read_cookie('auth');
        let url: string = hostUrl + 'hq/media/uploader';
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'POST',
            body: formData,
            headers: new Headers({
                // 'Content-Type' : 'multipart/form-data',
                'token' : token,
                'hq': 'hq'
            })
        });
        return await fetch(request)
            .then( (response: Response): Promise<any> => {
                return response.json()
            })
            .then( (data: any): { video?: string, image?: string, error?: string } => {
                if (data?.status === 'success') {
                    return data?.data;
                }
                if (data?.status === 'error') {
                    return { error: data?.message }
                }
            })
            .catch( (error: Error): any => {
                console.log(error);
                return { error: "Something has gone wrong!" };
            });
    }
    return Promise.resolve({
        'image': undefined,
        'video': undefined,
    });
}